import { TimelineItemBoxProps } from "@/app/activity/components/timeline/activityTimeline/type"
import {
  formatActivityBoxItems,
  getActivityBoxProps,
  getPositionX,
} from "@/app/activity/components/timeline/activityTimeline/utils"
import { createActivityCommonStore, createActivityStore } from "@/app/activity/store"
import { Activity } from "@/types/activity"
import { ActivityDurationData } from "@/types/data"
import { Session } from "@/types/session"
import { pointer } from "d3-selection"

import { RefObject, useMemo } from "react"

export const useGetActivityDurationDataBoxProps = ({
  past,
  current,
  future,
  dateStart,
  dateFinish,
  width,
}: {
  past?: ActivityDurationData[]
  current?: ActivityDurationData[]
  future?: ActivityDurationData[]
  dateStart?: Date
  dateFinish?: Date
  width: number
}) => {
  const result = [past, current, future]

  return useMemo(() => {
    return result.map((data) => {
      if (!data || !dateStart || !dateFinish) {
        return []
      }

      return data.map((activityData) =>
        getActivityBoxProps({ data: activityData, dateStart, dateFinish, width }),
      )
    })
  }, [...result, dateStart, dateFinish, width])
}

export const useGetActivityListProps = ({
  past,
  current,
  future,
  dateStart,
  dateFinish,
  width,
}: {
  past?: Activity[]
  current?: Activity[]
  future?: Activity[]
  dateStart?: Date
  dateFinish?: Date
  width: number
}) => {
  const result = [past, current, future]

  return useMemo(() => {
    return result.map((data) => {
      if (!data || !dateStart || !dateFinish) {
        return []
      }

      return formatActivityBoxItems(
        data.sort((a, b) => (a.idfActivity as number) - (b.idfActivity as number)) || [],
        dateStart,
        dateFinish,
        width,
      ) as TimelineItemBoxProps[]
    })
  }, [...result, dateStart, dateFinish, width])
}

export const useGetWidthPerTime = ({
  dateStart,
  dateFinish,
  chartWidth,
}: {
  dateStart?: Date
  dateFinish?: Date
  chartWidth: number
}) => {
  return useMemo(() => {
    if (chartWidth <= 0 || !dateStart || !dateFinish) {
      return 0
    }

    return chartWidth / (dateFinish.getTime() - dateStart.getTime())
  }, [dateStart, dateFinish, chartWidth])
}

export const useGetTimePerWidth = ({
  dateStart,
  dateFinish,
  chartWidth,
}: {
  dateStart?: Date
  dateFinish?: Date
  chartWidth: number
}) => {
  return useMemo(() => {
    if (chartWidth <= 0 || !dateStart || !dateFinish) {
      return 0
    }

    return (dateFinish.getTime() - dateStart.getTime()) / chartWidth
  }, [dateStart, dateFinish, chartWidth])
}

export const useUpdateDraggingSession = (chartWidth: number) => {
  const {
    timePerWidth,
    range: { dateStart, dateFinish },
  } = createActivityCommonStore((state) => ({
    timePerWidth: state.timePerWidth,
    range: state.range,
  }))

  const { activity } = createActivityStore((state) => ({
    activity: state.activity,
  }))

  if (!dateStart || !dateFinish) {
    return () => {
      return
    }
  }

  // 액티비티 시간 범위 (세션이 이 범위를 벗어나면 안됨)
  const activityStartTime = activity ? new Date(activity.dateStart).getTime() : null
  const activityFinishTime = activity ? new Date(activity.dateFinish).getTime() : null

  let initialSession: Session | null = null

  return ({
    event,
    ref,
    sessionRef,
    setTemporalSessionData,
    isDragStart = false,
    isDragEnd = false,
  }: {
    event: {
      direction: string
      moveX: number
      event?: MouseEvent
    }
    ref: RefObject<SVGSVGElement | null>
    sessionRef: RefObject<Session | undefined>
    setTemporalSessionData: (value: Session | undefined) => void
    isDragStart?: boolean
    isDragEnd?: boolean
  }) => {
    if (event.event) {
      const [mouseX] = pointer(event.event, ref.current as Element)

      if (mouseX < 0 || mouseX > chartWidth) {
        return
      }
    }

    if (isDragStart && sessionRef.current) {
      initialSession = { ...sessionRef.current }

      return
    }

    if (isDragEnd) {
      if (sessionRef.current) {
        const { direction, moveX } = event
        const newSession = { ...initialSession } as Session
        const movedTime = moveX * timePerWidth

        const initialStartTime = new Date(initialSession!.dateStart as string).getTime()
        const initialFinishTime = new Date(initialSession!.dateFinish as string).getTime()

        if (direction === "left") {
          let updateTime = initialStartTime + movedTime
          // 액티비티 시작 시간보다 작으면 액티비티 시작 시간으로 제한
          if (activityStartTime !== null && updateTime < activityStartTime) {
            updateTime = activityStartTime
          }
          if (updateTime < initialFinishTime) {
            newSession.dateStart = new Date(updateTime).toISOString()
          } else {
            newSession.dateStart = initialSession!.dateStart
          }
          newSession.dateFinish = initialSession!.dateFinish
        } else if (direction === "right") {
          let updateTime = initialFinishTime + movedTime
          // 액티비티 종료 시간보다 크면 액티비티 종료 시간으로 제한
          if (activityFinishTime !== null && updateTime > activityFinishTime) {
            updateTime = activityFinishTime
          }
          if (updateTime > initialStartTime) {
            newSession.dateFinish = new Date(updateTime).toISOString()
          } else {
            newSession.dateFinish = initialSession!.dateFinish
          }
          newSession.dateStart = initialSession!.dateStart
        }

        // Always use initialSession.dateStart for startX to prevent transform jitter
        const fixedStartXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(initialSession!.dateStart as string),
        })

        const endXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(newSession.dateFinish as string),
        })

        ;(newSession as TimelineItemBoxProps).startX = fixedStartXPos
        ;(newSession as TimelineItemBoxProps).width = endXPos - fixedStartXPos

        sessionRef.current = newSession
      }

      initialSession = null

      return
    }

    if (event && sessionRef.current && initialSession) {
      const { direction, moveX } = event

      const newSession = { ...initialSession } as Session
      const movedTime = moveX * timePerWidth

      const initialStartTime = new Date(initialSession.dateStart as string).getTime()
      const initialFinishTime = new Date(initialSession.dateFinish as string).getTime()

      switch (direction) {
        case "left": {
          let updateTime = initialStartTime + movedTime

          // 액티비티 시작 시간보다 작으면 액티비티 시작 시간으로 제한
          if (activityStartTime !== null && updateTime < activityStartTime) {
            updateTime = activityStartTime
          }

          if (updateTime < initialFinishTime) {
            newSession.dateStart = new Date(updateTime).toISOString()
          } else {
            newSession.dateStart = initialSession.dateStart
          }

          newSession.dateFinish = initialSession.dateFinish

          break
        }
        case "right": {
          let updateTime = initialFinishTime + movedTime

          // 액티비티 종료 시간보다 크면 액티비티 종료 시간으로 제한
          if (activityFinishTime !== null && updateTime > activityFinishTime) {
            updateTime = activityFinishTime
          }

          if (updateTime > initialStartTime) {
            newSession.dateFinish = new Date(updateTime).toISOString()
          } else {
            newSession.dateFinish = initialSession.dateFinish
          }

          newSession.dateStart = initialSession.dateStart

          break
        }
      }

      if (direction === "left") {
        const fixedEndXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(initialSession.dateFinish as string),
        })

        const newStartXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(newSession.dateStart as string),
        })

        ;(newSession as TimelineItemBoxProps).startX = newStartXPos
        ;(newSession as TimelineItemBoxProps).width = fixedEndXPos - newStartXPos
      } else {
        const fixedStartXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(initialSession.dateStart as string),
        })

        const newEndXPos = getPositionX({
          start: dateStart,
          end: dateFinish,
          width: chartWidth,
          date: new Date(newSession.dateFinish as string),
        })

        ;(newSession as TimelineItemBoxProps).startX = fixedStartXPos
        ;(newSession as TimelineItemBoxProps).width = newEndXPos - fixedStartXPos
      }

      setTemporalSessionData({ ...newSession })
    }
  }
}
